import type {
  KpiEntity,
  KpiValueEntity,
  ProjectKpiProgressEntity,
} from "../../domain/entities/KpiEntity.js";
import type {
  IKpiRepository,
  KpiCreateInput,
  KpiListOptions,
  KpiReorderInput,
  KpiUpdateInput,
  KpiValueCreateInput,
  KpiValueUpdateInput,
} from "../../domain/repositories/IKpiRepository.js";
import { KpiRemoteDataSource } from "../datasources/KpiRemoteDataSource.js";
import type { KpiDto, KpiValueDto } from "../dtos/KpiDto.js";

function mapKpiDto(dto: KpiDto): KpiEntity {
  return {
    id: dto.id,
    orgId: dto.org_id,
    projectId: dto.project_id,
    name: dto.name,
    unit: dto.unit,
    description: dto.description ?? null,
    projectValue: dto.project_value,
    sessionValue: dto.session_value ?? null,
    provinceValue: dto.province_value ?? null,
    outletValue: dto.outlet_value ?? null,
    categoryId: dto.category_id ?? null,
    sortOrder: dto.sort_order ?? 0,
    createdAt: dto.created_at,
    updatedAt: dto.updated_at,
    createdBy: dto.created_by,
  };
}

function mapValueDto(dto: KpiValueDto): KpiValueEntity {
  const sub = dto.submitter;
  const submitterName = sub
    ? [sub.first_name, sub.last_name].filter(Boolean).join(" ").trim() ||
      sub.email
    : null;
  return {
    id: dto.id,
    orgId: dto.org_id,
    projectId: dto.project_id,
    kpiId: dto.kpi_id,
    manualValue: dto.manual_value,
    systemValue: dto.system_value,
    locationId: dto.location_id ?? null,
    sessionId: dto.session_id ?? null,
    submissionId: dto.submission_id,
    createdAt: dto.created_at,
    createdBy: dto.created_by,
    locationName: dto.location?.name ?? null,
    submitterName: submitterName ?? null,
  };
}

export class KpiRepository implements IKpiRepository {
  constructor(private readonly ds: KpiRemoteDataSource) {}

  async getById(id: string): Promise<KpiEntity | null> {
    const dto = await this.ds.getKpiById(id);
    return dto ? mapKpiDto(dto) : null;
  }

  async list(
    options?: KpiListOptions,
  ): Promise<{ data: KpiEntity[]; total: number }> {
    const { data, total } = await this.ds.listKpis(options);
    return { data: data.map(mapKpiDto), total };
  }

  async create(input: KpiCreateInput): Promise<KpiEntity> {
    const dto = await this.ds.createKpi({
      org_id: input.orgId,
      project_id: input.projectId,
      name: input.name,
      unit: input.unit,
      project_value: input.projectValue,
      description: input.description ?? null,
      session_value: input.sessionValue ?? null,
      province_value: input.provinceValue ?? null,
      outlet_value: input.outletValue ?? null,
      category_id: input.categoryId ?? null,
      created_by: input.createdBy,
    });
    if (!dto) throw new Error("KPI creation failed");
    return mapKpiDto(dto);
  }

  async update(id: string, input: KpiUpdateInput): Promise<KpiEntity | null> {
    const payload: Partial<KpiDto> = {};
    if (input.name !== undefined) payload.name = input.name;
    if (input.unit !== undefined) payload.unit = input.unit;
    if (input.description !== undefined)
      payload.description = input.description;
    if (input.projectValue !== undefined)
      payload.project_value = input.projectValue;
    if (input.sessionValue !== undefined)
      payload.session_value = input.sessionValue;
    if (input.provinceValue !== undefined)
      payload.province_value = input.provinceValue;
    if (input.outletValue !== undefined)
      payload.outlet_value = input.outletValue;
    if (input.categoryId !== undefined) payload.category_id = input.categoryId;
    if (input.sortOrder !== undefined) payload.sort_order = input.sortOrder;
    const dto = await this.ds.updateKpi(id, payload);
    return dto ? mapKpiDto(dto) : null;
  }

  async delete(id: string): Promise<void> {
    await this.ds.deleteKpi(id);
  }

  async reorder(updates: KpiReorderInput): Promise<void> {
    return this.ds.reorderKpis(updates);
  }

  async listValues(kpiId: string): Promise<KpiValueEntity[]> {
    return (await this.ds.listKpiValues(kpiId)).map(mapValueDto);
  }

  async updateKpiValue(
    id: string,
    input: KpiValueUpdateInput,
  ): Promise<KpiValueEntity> {
    return mapValueDto(
      await this.ds.updateKpiValue(id, {
        manual_value: input.manualValue,
        location_id: input.locationId ?? null,
        session_id: input.sessionId ?? null,
      }),
    );
  }

  async deleteKpiValue(id: string): Promise<void> {
    await this.ds.deleteKpiValue(id);
  }

  async createKpiValue(input: KpiValueCreateInput): Promise<KpiValueEntity> {
    const dto = await this.ds.createKpiValue({
      org_id: input.orgId,
      project_id: input.projectId,
      kpi_id: input.kpiId,
      manual_value: input.manualValue,
      created_by: input.createdBy,
      location_id: input.locationId ?? null,
      session_id: input.sessionId ?? null,
      submission_id: input.submissionId,
    });
    return mapValueDto(dto);
  }

  async listValuesByProject(
    orgId: string,
    projectId: string,
    filters?: {
      locationId?: string;
      createdBy?: string;
      dateFrom?: string;
      dateTo?: string;
    },
  ): Promise<KpiValueEntity[]> {
    return (
      await this.ds.listKpiValuesByProject(orgId, projectId, filters)
    ).map(mapValueDto);
  }

  async listProjectProgress(
    orgId: string,
    projectIds: string[],
  ): Promise<ProjectKpiProgressEntity[]> {
    return (await this.ds.listProjectProgress(orgId, projectIds)).map((row) => ({
      projectId: row.project_id,
      progressPct: Number(row.progress_pct) || 0,
    }));
  }
}
